import { useCallback, useEffect, useMemo, useState } from "react";
import { QuizApi } from "../../../../api/quiz";
import { TeacherAPI } from "../../../../api/teacherAPI";
import { useAuth } from "../../../../hooks";

// Controller hook del Dashboard de Evaluaciones del profesor.
// Centraliza: clases del profesor (classrole), resumen de clase (E1),
// historial de examenes de un alumno (E2) y ranking por examen+clase.
// Todo READ-ONLY contra los endpoints del backend F1.
export function useTeacherEvalDashboard() {
  const { accessToken, user } = useAuth();
  const teacherId = user?.user?.teacher;

  const quizApi = useMemo(() => new QuizApi(), []);
  const teacherApi = useMemo(() => new TeacherAPI(), []);

  // Clases del profesor
  const [classNumbers, setClassNumbers] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  // Examenes del profesor (para mini-listado del apartado Examenes)
  const [quizzes, setQuizzes] = useState([]);

  // Resumen de clase (E1)
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Historial de examenes de un alumno (E2)
  const [studentExams, setStudentExams] = useState(null);
  const [loadingExams, setLoadingExams] = useState(false);

  // Cargar clases una vez
  useEffect(() => {
    let active = true;
    (async () => {
      if (!accessToken || !teacherId) return;
      try {
        setLoadingClasses(true);
        const res = await teacherApi.getTeacherById(accessToken, teacherId);
        const roles = Array.isArray(res?.teacher?.classrole)
          ? res.teacher.classrole
          : [];
        const classes = [
          ...new Set(roles.map((r) => r.classnumber).filter(Boolean)),
        ];
        if (active) setClassNumbers(classes);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error cargando clases del teacher:", err);
        if (active) setClassNumbers([]);
      } finally {
        if (active) setLoadingClasses(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, teacherApi, teacherId]);

  // Cargar examenes del profesor una vez
  useEffect(() => {
    let active = true;
    (async () => {
      if (!accessToken || !teacherId) return;
      try {
        const res = await quizApi.getAllQuizzes(accessToken, teacherId);
        if (active) setQuizzes(res?.quizzes || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error cargando examenes del teacher:", err);
        if (active) setQuizzes([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, quizApi, teacherId]);

  const loadClassSummary = useCallback(
    async (classnumber) => {
      if (!accessToken || !teacherId || !classnumber) return;
      try {
        setLoadingSummary(true);
        const res = await quizApi.getClassSummary(
          accessToken,
          teacherId,
          classnumber,
        );
        setSummary(res?.data || null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error cargando resumen de clase:", err);
        setSummary(null);
      } finally {
        setLoadingSummary(false);
      }
    },
    [accessToken, quizApi, teacherId],
  );

  const loadStudentExams = useCallback(
    async (studentId) => {
      if (!accessToken || !teacherId || !studentId) return;
      try {
        setLoadingExams(true);
        setStudentExams(null);
        const res = await quizApi.getStudentExams(
          accessToken,
          studentId,
          teacherId,
        );
        setStudentExams(res?.data || null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error cargando examenes del alumno:", err);
        setStudentExams(null);
      } finally {
        setLoadingExams(false);
      }
    },
    [accessToken, quizApi, teacherId],
  );

  const loadRanking = useCallback(
    async (quizId, classnumber) => {
      if (!accessToken || !teacherId || !quizId || !classnumber) return [];
      try {
        const res = await quizApi.getRankingResults(
          accessToken,
          teacherId,
          quizId,
          classnumber,
        );
        return res?.ranking || [];
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error cargando ranking:", err);
        return [];
      }
    },
    [accessToken, quizApi, teacherId],
  );

  return {
    teacherId,
    classNumbers,
    loadingClasses,
    quizzes,
    summary,
    loadingSummary,
    loadClassSummary,
    studentExams,
    loadingExams,
    loadStudentExams,
    loadRanking,
  };
}
